import './StatusDropdown.scss';

import clsx from 'clsx';
import _ from 'lodash';
import React, { ReactNode, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import Select from '@/src/components/common/Select/Select';
import UndoToast from '@/src/components/common/Toast/UndoToast';
import { toast } from '@/src/reducers/toast/toast.actions';
import { cn } from '@/src/util/ui';

import { InboxEntryStatus } from '../../../../types';

type StatusProps = {
  value: InboxEntryStatus;
  onSelect?: (selectedOption: any) => void;
  onSelectOption?: (selectedOption: any) => Promise<any>;
  isDisabled: boolean;
  isDuplicate: boolean;
  className?: string;
  selectClassName?: {
    main?: string;
    options?: string;
    trigger?: string;
  };
  tooltip?: {
    id: string;
    component: ReactNode;
  };
  children?: ReactNode;
};

export const STATUS_FILTERS = {
  NEW: [
    { value: InboxEntryStatus.Open, label: 'Open' },
    { value: InboxEntryStatus.Closed, label: 'Close' },
  ],
  OPEN: [
    { value: InboxEntryStatus.Open, label: 'Open' },
    { value: InboxEntryStatus.Closed, label: 'Close' },
  ],
  CLOSED: [
    { value: InboxEntryStatus.Open, label: 'Re-open' },
    { value: InboxEntryStatus.Closed, label: 'Close' },
  ],
};

const StatusDropdown = ({
  value,
  className,
  selectClassName,
  isDisabled,
  onSelect,
  isDuplicate,
  onSelectOption,
  tooltip,
  children,
}: StatusProps) => {
  const isNew = value === InboxEntryStatus.New;
  const isClosed = value === InboxEntryStatus.Closed;
  const customClasses = clsx(
    className,
    isDisabled && 'disabled',
    isClosed && 'closed',
    value !== InboxEntryStatus.Closed && value
  );

  const tooltipProps = tooltip
    ? {
        'data-tooltip-id': tooltip.id,
      }
    : {};
  const filterOptions = useMemo(() => {
    return STATUS_FILTERS[_.upperCase(value)].filter(
      (f) => f.value.toLowerCase() !== value.toLowerCase()
    );
  }, [value]);
  const dispatch = useDispatch();

  const handleSelect = useCallback(
    async (newStatus) => {
      if (onSelect) {
        onSelect(newStatus);
      }
    },
    [onSelect]
  );

  const handleClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleUpdateStatus = useCallback((newStatus, undo = false) => {
    const label = newStatus.value === InboxEntryStatus.Open ? 're-opened' : 'closed';
    const status = _.upperFirst(newStatus.value);
    const toastMessage = `Successfully ${label} the entry.`;

    const undoStatus = STATUS_FILTERS[_.upperCase(status)].find((f) => f.value !== status);
    dispatch(
      toast(
        <UndoToast
          customClass={`inboxToast`}
          label={toastMessage}
          onUndo={() => handleSelectOption(undoStatus, true)}
          undoDone={undo}
        />
      )
    );
  }, []);

  const handleSelectOption = useCallback(
    async (newStatus, undo = false) => {
      const response = await onSelectOption(newStatus);

      if (response) {
        handleUpdateStatus(newStatus, undo);
      }
      return response;
    },
    [onSelectOption, handleUpdateStatus]
  );

  if (isDuplicate) {
    return (
      <div className={cn('status-entry', className)} {...tooltipProps}>
        <div
          className={cn(
            'container--duplicate',
            'tw-flex tw-items-center tw-justify-center',
            'tw-w-[95px]',
            'tw-bg-cfo-grey-300',
            'tw-border-none',
            'tw-font-brandonTextMedium tw-text-cfo-grey-600',
            'tw-px-[10px] tw-py-[1px] tw-mx-[5px]',
            'tw-rounded-[34px] tw-leading-[20px]',
            className
          )}
        >
          <span>Duplicate</span>
        </div>
        {children}
      </div>
    );
  }

  if (isNew) {
    return (
      <div className={cn('status-entry', className)} {...tooltipProps}>
        <div
          className={cn(
            'container--newStatus',
            'tw-flex tw-items-center tw-justify-center',
            'tw-w-[95px]',
            'tw-bg-cfo-blue-200',
            'tw-border-solid tw-border-cfo-blue-700 tw-border-[1px]',
            'tw-font-brandonTextMedium tw-text-cfo-blue-700',
            'tw-px-[10px] tw-py-[1px] tw-mx-[5px]',
            'tw-rounded-[34px] tw-leading-[20px]',
            className
          )}
        >
          <span>New</span>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn('container--entryStatus', customClasses)}
      {...tooltipProps}
      role="button"
      tabIndex={0}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Select
        key={`statusdropdown_${value}`}
        classes={{
          ...selectClassName,
          options: clsx(selectClassName?.options, 'container--entryStatusOptions'),
        }}
        selected={{ value: value, label: _.upperFirst(value) }}
        options={filterOptions}
        onChange={handleSelect}
        onSelectOption={handleSelectOption}
        onClick={handleClick}
        disabled={isDisabled}
        hideIcon={isDisabled}
      />
      {tooltip && tooltip.component}
      {children}
    </div>
  );
};

export default StatusDropdown;
